import React, { useEffect, useState, ChangeEvent, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
} from 'reactstrap';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { useUser } from 'context/UserContext';
import { useAlertPlus, useConfirm } from 'hooks';
import DatePicker, { DatePickerChangeEvent } from 'components/DatePicker';
import Required from 'components/Required';
import ConfirmModal from 'components/ConfirmModal';
import AlertModal from 'components/AlertModal';
import DiagnosticoSelect from 'components/Authorizations/IOMA/DiagnosticoSelect';
import { getAllConsultoriosCokiba } from 'features/consultorios/consultoriosAPI';
import { ConsultorioCokiba } from 'types/Consultorio';
import {
  getAutorizacionIOMAById,
  getAfiliadoIOMAByNro,
  postAutorizacionIOMA,
} from 'features/autorizacionesioma/autorizacionesiomaAPI';
import { AutorizacionIOMA } from 'types/AutorizacionesIOMA';
import { getDiagnosticos } from 'features/diagnosticos/diagnosticosAPI';
import { Diagnostico } from 'types/Diagnosticos';

export default function CreateConsultorio() {
  const { estado, id } = useParams() as {
    estado?: string,
    id?: number,
  };
  const { getMatricula } = useUser();
  const matriculateId = getMatricula();
  if (!matriculateId) {
    return null;
  }

  const history = useHistory();
  const location = useLocation<{ prevOptions?: any }>();

  const [formValidation, setFormValidation] = useState<
    Partial<Record<keyof AutorizacionIOMA, boolean>>
  >({});

  const [disableSend, setDisableSend] = useState(false);

  const [isLoading, setLoading] = useState(true);

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const formElement = useRef<HTMLFormElement | null>(null);

  const [consultorios, setConsultorios] = useState<ConsultorioCokiba[]>([]);
  const [diagnosticos, setDiagnosticos] = useState<Diagnostico[]>([]);

  const [data, setData] = useState<AutorizacionIOMA>({
    idAutorizacion: null,
    matricula: +matriculateId,
    consultorioId: null,
    numeroAfiliado: null,
    apellidoNombre: null,
    diagnostico: '',
    evolucion: null,
    fechaOrden: null,
    sesiones: null,
    bono1: null,
    bono2: null,
    discapacidad: false,
    resultado: null,
    modulo: null,
    nroAuto: null,
    fechaAuto: null,
  });

  useEffect(() => {
    setLoading(true);

    const loadData = async () => {
      try {
        const responseConsultorios: ConsultorioCokiba[] = await getAllConsultoriosCokiba() as ConsultorioCokiba[];
        if (responseConsultorios) {
          setConsultorios(responseConsultorios);
        }

        const responseDiagnosticos: Diagnostico[] = await getDiagnosticos() as Diagnostico[];
        if (responseDiagnosticos) {
          setDiagnosticos(responseDiagnosticos);
        }
      } catch (err) {
        setDisableSend(true);
        alert(
          'Se produjo un error al intentar recuperar información inicial. ' +
            'Por favor, intente nuevamente más tarde.',
        );
        console.error('ConsultoriosCreate', 'Error al cargar datos iniciales');
        console.info(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    console.debug(location);
  }, [location]);

  useEffect(() => {
    setLoading(true);

    const loadData = async () => {
      try {
        const response: AutorizacionIOMA =
          (await getAutorizacionIOMAById(matriculateId, estado ?? '', id ?? 0)) as AutorizacionIOMA;

        if (response) {
          const fechaOrden = moment(response.fechaOrden);
          let fechaValida: Date | null = fechaOrden.toDate();

          if (estado != 'autorizadas' && response.fechaOrden) {
            const limite30Dias = moment().subtract(30, 'days').startOf('day');

            if (!fechaOrden.isSameOrAfter(limite30Dias)) {
              // La fecha de orden no es válida, se fuerza a null para que el usuario la ingrese nuevamente
              fechaValida = null;
            }
          }

          setData({
            ...response,
            fechaOrden: fechaValida,
          });
        } else {
          alert(
            'Se produjo un error al intentar recuperar información inicial. ' +
              'Por favor, intente nuevamente más tarde.',
          );
          setDisableSend(true);
        }
      } catch (err) {
        setDisableSend(true);
        alert(
          'Se produjo un error al intentar recuperar información inicial. ' +
            'Por favor, intente nuevamente más tarde.',
        );
        console.error('ConsultoriosCreate', 'Error al cargar datos iniciales');
        console.info(err);
      } finally {
        setLoading(false);
      }
    };

    if (!!id && !!estado) {
      loadData();

      if (estado == 'autorizadas') {
        setDisableSend(true);
      }
    }
  }, [id]);

  const handleFormValidation = () => {
    let newFormValidation = {};

    let toValidate = [
      ...Array.from(document.getElementsByClassName('form-control')),
    ] as HTMLInputElement[];
    toValidate = toValidate.filter(e => !e.disabled && e.required);

    let missing = false;
    // Validar campos required
    toValidate.forEach(field => {
      if (field.required && !field.disabled && !field.value?.trim()) {
        field.classList.add('is-invalid');

        missing = true;
      } else if (field.inputMode === 'numeric') {
        if (!/^\d+$/.test(field.value)) {
          newFormValidation = {
            ...newFormValidation,
            [field.name]: false,
          };
        }
      } else {
        field.classList.remove('is-invalid');
      }
    });

    if (missing) {
      setFormValidation(newFormValidation);
      alert(
        'Todos los campos marcados con * son obligatorios. ' +
          'Por favor complete los faltantes antes de enviar la solicitud.',
      );
      return false;
    }

    // Validar que la fecha de orden sea una fecha real y dentro del rango permitido
    const fechaOrden = data.fechaOrden ? moment(data.fechaOrden) : null;
    if (
      !fechaOrden?.isValid() ||
      fechaOrden.isBefore(moment().subtract(30, 'days').startOf('day')) ||
      fechaOrden.isAfter(moment().endOf('day'))
    ) {
      document.getElementById('fechaOrden')?.classList.add('is-invalid');
      alert(
        'La Fecha Orden ingresada no es válida. ' +
          'Por favor, selecciónela desde el calendario.',
      );
      return false;
    }

    return true;
  };

  // Confirmación de envío de Solicitud
  const handleSendForm = () => {
    if (!handleFormValidation()) {
      return;
    }

    setConfirm(
      true,
      '¿Estás seguro que deseas enviar esta solicitud de autorización IOMA?',
      'send',
    );
  };

  // Gestor de respuestas de confirmaciones
  const handleConfirmation = (action: string) => {
    if (action === 'send') {
      handleSend();
    }

    setConfirm(false);
  };

  // Envío de solicitudes
  const handleSend = async () => {
    setFormValidation({});

    setLoading(true);

    // Preparamos los inputs menos los archivos
    const autorizacion: Partial<AutorizacionIOMA> = {};

    if (!formElement.current) return;

    new FormData(formElement.current).forEach((value, key) => {
      // @ts-ignore
      autorizacion[key as keyof AutorizacionIOMA] = value.toString() as any;
    });

    // La fecha se toma del estado y no del texto del input, que puede no ser una fecha válida
    autorizacion.fechaOrden = moment(data.fechaOrden).format('DD/MM/YYYY');

    // Enviamos la petición
    await postAutorizacionIOMA(autorizacion)
      .then(res => {
        if (!res || res.status != 'ok') {
          // Error interno
          console.error('ConsultoriosCreate', 'Error al enviar la solicitud');
          console.info(res);
          alert(
            res.payload?.message ??
            'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
          );
          return;
        }

        alert('La solicitud se envió correctamente', () => {
          history.push('/solicitudes/ioma');
        });
      })
      .catch(err => {
        console.error(
          'ConsultoriosCreate',
          'Error inesperado al intentar enviar la solicitud',
        );
        console.info(err);
        alert(
          'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const setApellidoNombre = async (numeroAfiliado: string) => {
    setLoading(true);

    await getAfiliadoIOMAByNro(numeroAfiliado)
      .then(res => {
        if (!res) {
          alert(`El Número de Afiliado ingresado no se encuentra en padrón. No se pudo completar el pedido.
            Envíe por email constancia afiliatoria y los datos de la orden médica.`);

          setData(prev => ({ ...prev, apellidoNombre: '' }));
          return;
        }

        setData(prev => ({
          ...prev,
          apellidoNombre: res?.nombreCompleto?.replace(/['`´,]/g, ' ') ?? '',
        }));
      })
      .catch(err => {
        console.error(
          'setApellidoNombre',
          `El Número de Afiliado ingresado no se encuentra en padrón. No se pudo completar el pedido.
            Envíe por email constancia afiliatoria y los datos de la orden médica.`,
        );
        console.info(err);
        alert(`El Número de Afiliado ingresado no se encuentra en padrón. No se pudo completar el pedido.
          Envíe por email constancia afiliatoria y los datos de la orden médica.`);

        setData(prev => ({ ...prev, apellidoNombre: '' }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement> | DatePickerChangeEvent,
  ) => {
    const { target } = event;

    const name = target.name;

    let value: string | boolean | number | Date | null;
    value = target.type === 'checkbox' ? target.checked : target.value;
    if (
      typeof value === 'string' &&
      [
        'consultorioId',
        'evolucion',
        'sesiones',
        'bono1',
        'bono2',
      ].includes(name)
    ) {
      value = !Number.isNaN(parseInt(value, 10)) ? parseInt(value, 10) : '';
    }

    if (name === 'sesiones' && typeof value === 'number') {
      if (value > 12) {
        value = 12;
      } else if (value < 1) {
        value = 1;
      }
    }

    if (name === 'numeroAfiliado' && typeof value === 'string') {
      const clean = value.toUpperCase().replace(/[^0-9A-Z]/g, '');
      const leadingLetters = clean.match(/^[A-Z]*/)?.[0] ?? '';
      const digits = clean.slice(leadingLetters.length).replace(/[A-Z]/g, '');
      value = (leadingLetters + digits).slice(0, 12);
    }

    // Guardamos el nuevo valor
    setData(prev => ({
      ...prev,
      [name]: value,
    }));

    if (
      name === 'numeroAfiliado' &&
      typeof value === 'string' &&
      value.length === 12
    ) {
      setApellidoNombre(value);
    } else if (name === 'numeroAfiliado') {
      setData(prev => ({
        ...prev,
        apellidoNombre: '',
      }));
    }
  };

  return (
    <div className="content">
      <Row>
        <Col sm={12}>
          <Card>
            <CardHeader>
              <Row style={{ alignItems: 'center' }}>
                <Col sm={8}>
                  <CardTitle tag="h5">Autorización IOMA</CardTitle>
                </Col>
                <Col sm={4} className="text-right">
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => {
                      history.push({
                        pathname: '/solicitudes/ioma',
                        state: location.state,
                      });
                    }}
                  >
                    Volver
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Form innerRef={formElement}>
                <Col sm={12}>
                  <CardTitle>Autorización</CardTitle>
                </Col>
                <FormGroup>
                  <Row>
                    <Col sm={6}>
                      <Label for="numeroAfiliado">
                        Número de Afiliado <Required />
                      </Label>
                      <Input
                        id="numeroAfiliado"
                        name="numeroAfiliado"
                        value={data.numeroAfiliado ?? ''}
                        invalid={formValidation.numeroAfiliado === false}
                        onChange={handleInputChange}
                        disabled={isLoading || disableSend}
                        required
                      />
                    </Col>
                    <Col sm={6}>
                      <Label for="apellidoNombre">
                        Apellido y Nombre <Required />
                      </Label>
                      <Input
                        id="apellidoNombre"
                        name="apellidoNombre"
                        value={data.apellidoNombre ?? ''}
                        disabled={isLoading || disableSend}
                        readOnly
                        required
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col md="6" className='mt-4'>
                      <Input
                        id="discapacidad"
                        name="discapacidad"
                        type="checkbox"
                        checked={data.discapacidad === true}
                        invalid={formValidation.discapacidad === false}
                        onChange={handleInputChange}
                        disabled={isLoading || disableSend}
                      />
                      <Label
                        for="discapacidad"
                        style={{ marginLeft: 5 }}
                      >
                        Es discapacidad
                      </Label>
                    </Col>
                    <Col md="6">
                      <Label for="sesiones">
                        Sesiones <Required />
                      </Label>
                      { !data.discapacidad
                        ?
                          <Input
                            id="sesiones"
                            name="sesiones"
                            type="select"
                            className="custom-select form-control"
                            value={data.sesiones ?? ''}
                            invalid={formValidation.sesiones === false}
                            onChange={handleInputChange}
                            disabled={isLoading || disableSend}
                            required
                          >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                          </Input>
                        :
                          <Input
                            id="sesiones"
                            name="sesiones"
                            type="number"
                            value={data.sesiones ?? ''}
                            invalid={formValidation.sesiones === false}
                            onChange={handleInputChange}
                            disabled={isLoading || disableSend}
                            min={1}
                            max={12}
                            required
                          />
                      }
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={6}>
                      <DiagnosticoSelect
                        diagnosticos={diagnosticos}
                        data={data}
                        handleInputChange={handleInputChange}
                        isLoading={isLoading || disableSend}
                        formValidation={formValidation}
                      />
                    </Col>
                    <Col md="6">
                      <Label for="evolucion">
                        Evolución <Required />
                      </Label>
                      <Input
                        id="evolucion"
                        name="evolucion"
                        type="select"
                        className="custom-select form-control"
                        value={data.evolucion ?? ''}
                        invalid={formValidation.evolucion === false}
                        onChange={handleInputChange}
                        disabled={isLoading || disableSend}
                        required
                      >
                        <option value={0}>Sin evolución</option>
                        <option value={3}>Menor a 3 años</option>
                        <option value={4}>4 años o más</option>
                      </Input>
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={6}>
                      <Label for="fechaOrden">
                        Fecha Orden <Required />
                      </Label>
                      <DatePicker
                        id="fechaOrden"
                        name="fechaOrden"
                        selected={data.fechaOrden ?? ''}
                        dateFormat="dd/MM/yyyy"
                        locale="es"
                        placeholderText="Fecha Orden"
                        className="date-picker form-control"
                        minDate={moment().subtract(30, 'days').toDate()}
                        maxDate={new Date()}
                        disabled={isLoading || disableSend}
                        onChange={date => {
                          handleInputChange({
                            target: {
                              name: 'fechaOrden',
                              type: 'datepicker',
                              value: date,
                            },
                          });
                        }}
                        autoComplete="off"
                        required
                      />
                    </Col>
                    <Col md="6">
                      <Label for="consultorioId">
                        Consultorio <Required />
                      </Label>
                      <Input
                        id="consultorioId"
                        name="consultorioId"
                        type="select"
                        className="custom-select form-control"
                        value={data.consultorioId ?? ''}
                        invalid={formValidation.consultorioId === false}
                        onChange={handleInputChange}
                        disabled={isLoading || disableSend}
                        required
                      >
                        <option value="">Seleccione una opción...</option>
                        {consultorios?.map(item => {
                          return (
                            <option
                              key={item.idConsultorio}
                              value={item.idConsultorio}
                            >
                              {item.direccion} {item.numero}, {item.localidad}
                            </option>
                          );
                        })}
                      </Input>
                    </Col>
                  </Row>
                </FormGroup>
                { !data.discapacidad
                  ?
                    <FormGroup>
                      <Row>
                        <Col sm={6}>
                          <Label for="bono1">Bono 1 <Required /></Label>
                          <Input
                            id="bono1"
                            name="bono1"
                            type="number"
                            value={data.bono1 ?? ''}
                            invalid={formValidation.bono1 === false}
                            onChange={handleInputChange}
                            disabled={isLoading || disableSend}
                            required
                          />
                        </Col>
                        <Col sm={6}>
                          <Label for="bono2">Bono 2</Label>
                          <Input
                            id="bono2"
                            name="bono2"
                            type="number"
                            value={data.bono2 ?? ''}
                            invalid={formValidation.bono2 === false}
                            onChange={handleInputChange}
                            disabled={isLoading || disableSend}
                          />
                        </Col>
                      </Row>
                    </FormGroup>
                  : null
                }
                { estado == 'autorizadas'
                  ?
                    <>
                      <FormGroup>
                        <Row>
                          <Col sm={6}>
                            <Label for="modulo">Módulo</Label>
                            <Input
                              id="modulo"
                              name="modulo"
                              type="text"
                              value={data.modulo ?? ''}
                              invalid={formValidation.modulo === false}
                              onChange={handleInputChange}
                              disabled={isLoading || disableSend}
                              required
                            />
                          </Col>
                          <Col sm={6}>
                            <Label for="nroAuto">Nro. de Autorización</Label>
                            <Input
                              id="nroAuto"
                              name="nroAuto"
                              type="number"
                              value={data.nroAuto ?? ''}
                              invalid={formValidation.nroAuto === false}
                              onChange={handleInputChange}
                              disabled={isLoading || disableSend}
                            />
                          </Col>
                        </Row>
                      </FormGroup>
                      <FormGroup>
                        <Row>
                          <Col sm={6}>
                            <Label for="fechaAuto">Fecha de Autorización</Label>
                            <Input
                              id="fechaAuto"
                              name="fechaAuto"
                              type="text"
                              value={data.fechaAuto ? moment(data.fechaAuto, 'YYYY-MM-DD').format('DD/MM/YYYY') : ''}
                              invalid={formValidation.fechaAuto === false}
                              onChange={handleInputChange}
                              disabled={isLoading || disableSend}
                              required
                            />
                          </Col>
                          <Col sm={6}></Col>
                        </Row>
                      </FormGroup>
                    </>
                  : null
                }
                { estado == 'observadas'
                  ?
                    <FormGroup>
                      <Row>
                        <Col sm={6}>
                          <Label for="resultado">Resultado</Label>
                          <Input
                            id="resultado"
                            name="resultado"
                            type="text"
                            value={data.resultado ?? ''}
                            invalid={formValidation.resultado === false}
                            onChange={handleInputChange}
                            disabled={true}
                            required
                          />
                        </Col>
                        <Col sm={6}></Col>
                      </Row>
                    </FormGroup>
                  : null
                }
                { !disableSend
                  ?
                    <FormGroup>
                      <div className="text-center">
                        <Button
                          type="button"
                          className="btn-round"
                          onClick={handleSendForm}
                          disabled={isLoading || disableSend}
                        >
                          Autorizar
                        </Button>
                      </div>
                    </FormGroup>
                  : null
                }
              </Form>
              <AlertModal
                isOpen={showAlert}
                message={message}
                onClose={alertCallback}
              />
              <ConfirmModal
                isOpen={confirm}
                title={confirmTitle}
                action={confirmAction}
                onClose={() => setConfirm(false)}
                onConfirm={handleConfirmation}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
